import type { FieldSelection, RelationQuery } from '#layers/data-model/types/field-selection';
import type { ResolvedMonospaceCollection } from '#layers/data-model/types/schema/resolved-schema';
import type { QuerySchemaOutput, QuerySchemaRead } from '#layers/data-model/utils/schema/query-schema';
import { pick } from '#layers/base/public';
import { findCollectionPrimitiveFieldByApiName, findCollectionRelationFieldByApiName } from '#layers/data-model/utils/fields/fields';
import { outputReturnsFieldPath } from '#layers/data-model/utils/schema/read-output';
import { RelationRowReads, resolveRelationIncludeRead } from '#layers/data-model/utils/schema/relation-preview';

/**
 * Drops the fields, relation includes, and relation arguments an operation does not accept; the engine
 * rejects the whole request for any of them. `*` stays, since the engine expands it to the returned fields.
 * A preview include of a relation that cannot cap its rows is dropped as well ({@link resolveRelationIncludeRead}).
 */
export function narrowFieldSelectionToOutput(collection: ResolvedMonospaceCollection, selection: FieldSelection, output: QuerySchemaOutput): FieldSelection {
	function returns(name: string) {
		if (name === '*') return true;
		const field = findCollectionPrimitiveFieldByApiName(collection, name) ?? findCollectionRelationFieldByApiName(collection, name);
		return !!field && outputReturnsFieldPath(output, collection, [field.id]);
	}

	const narrowed: FieldSelection = { ...selection };
	if (selection.fields) narrowed.fields = selection.fields.filter((name) => returns(name));

	if (selection.include) {
		narrowed.include = Object.fromEntries(Object.entries(selection.include).flatMap(([name, query]) => {
			const relation = findCollectionRelationFieldByApiName(collection, name);
			const read = relation && output.relationReadById(relation.id);
			const nested = relation?.oppositeCollection && read && narrowRelationQuery(relation.oppositeCollection, query, read);
			return nested ? [[name, nested]] : [];
		}));
	}

	return narrowed;
}

type RelationArgument = Exclude<keyof RelationQuery, keyof FieldSelection | 'limit'>;

/** Which relation arguments besides `limit` the relation's read accepts; a new argument needs an entry. */
function acceptedRelationArguments(read: QuerySchemaRead) {
	return {
		offset: read.pagination(),
		filter: read.filter() !== undefined,
		sort: read.sorting() !== undefined,
	} satisfies Record<RelationArgument, boolean>;
}

/** The include's row limit as the relation's read accepts it, or `undefined` to leave the relation out. */
function rowLimitArgument(query: RelationQuery, read: QuerySchemaRead): Pick<RelationQuery, 'limit'> | undefined {
	const rows = resolveRelationIncludeRead(query.limit, read);

	switch (rows.kind) {
		case RelationRowReads.Every:
			return {};
		case RelationRowReads.Capped:
			return { limit: rows.limit };
		case RelationRowReads.Unavailable:
			return undefined;
		default:
			return rows satisfies never;
	}
}

/**
 * The include as the relation's read accepts it, or `undefined` to leave the relation out. Arguments
 * are copied only when accepted, so one without an entry above is never sent.
 */
function narrowRelationQuery(collection: ResolvedMonospaceCollection, query: RelationQuery, read: QuerySchemaRead): RelationQuery | undefined {
	const output = read.output();
	const rowLimit = rowLimitArgument(query, read);
	if (!output || !rowLimit) return undefined;
	const accepted = acceptedRelationArguments(read);

	return {
		...narrowFieldSelectionToOutput(collection, pick(query, ['fields', 'include']), output),
		...rowLimit,
		...(accepted.offset ? pick(query, ['offset']) : {}),
		...(accepted.filter ? pick(query, ['filter']) : {}),
		...(accepted.sort ? pick(query, ['sort']) : {}),
	};
}
